import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Lightbulb,
  Link2,
  AlertTriangle,
  Flag,
  Zap,
  Check,
  X,
  ChevronDown,
  ChevronUp,
  Filter,
  RefreshCw,
  Loader2,
  MapPin,
  Shield,
  Sparkles,
} from 'lucide-react';

const INSIGHT_CONFIG = {
  bundeling: { label: 'Bundeling', icon: Link2, color: '#2E75B6', bg: 'bg-blue-500/10', text: 'text-blue-600 dark:text-blue-400', border: 'border-blue-500/20' },
  missing: { label: 'Ontbreekt', icon: AlertTriangle, color: '#EA580C', bg: 'bg-orange-500/10', text: 'text-orange-600 dark:text-orange-400', border: 'border-orange-500/20' },
  risk: { label: 'Risico', icon: Flag, color: '#DC2626', bg: 'bg-red-500/10', text: 'text-red-600 dark:text-red-400', border: 'border-red-500/20' },
  optimization: { label: 'Optimalisatie', icon: Zap, color: '#16A34A', bg: 'bg-green-500/10', text: 'text-green-600 dark:text-green-400', border: 'border-green-500/20' },
};

const STATUS_CONFIG = {
  new: { label: 'Nieuw', class: 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300' },
  accepted: { label: 'Geaccepteerd', class: 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300' },
  dismissed: { label: 'Afgewezen', class: 'bg-slate-100 text-slate-500 dark:bg-slate-500/20 dark:text-slate-400' },
};

const CONFIDENCE_CONFIG = {
  high: { label: 'Hoog', class: 'bg-green-500', width: 'w-full' },
  medium: { label: 'Gemiddeld', class: 'bg-yellow-500', width: 'w-2/3' },
  low: { label: 'Laag', class: 'bg-slate-400', width: 'w-1/3' },
};

function InsightCard({ insight, onUpdate, updating }) {
  const [expanded, setExpanded] = useState(false);
  const config = INSIGHT_CONFIG[insight.type] || INSIGHT_CONFIG.optimization;
  const Icon = config.icon;
  const statusCfg = STATUS_CONFIG[insight.status] || STATUS_CONFIG.new;
  const confCfg = CONFIDENCE_CONFIG[insight.confidence] || CONFIDENCE_CONFIG.medium;

  return (
    <div
      data-testid={`insight-card-${insight.insight_id}`}
      className={`bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#21262D] overflow-hidden transition-all duration-200 ${insight.status === 'dismissed' ? 'opacity-60' : ''}`}
    >
      <button
        data-testid={`insight-toggle-${insight.insight_id}`}
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-start gap-3 p-4 text-left hover:bg-slate-50/50 dark:hover:bg-[#1C2128] transition-colors"
      >
        <div className={`w-9 h-9 rounded-lg ${config.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
          <Icon className="w-4 h-4" style={{ color: config.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${config.bg} ${config.text}`}>
              {config.label}
            </span>
            <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${statusCfg.class}`}>
              {statusCfg.label}
            </span>
          </div>
          <h4 className="text-sm font-semibold text-slate-900 dark:text-[#E6EDF3] leading-snug">{insight.title}</h4>
          {!expanded && (
            <p className="text-xs text-slate-500 dark:text-[#8B949E] mt-1 line-clamp-1">{insight.description}</p>
          )}
        </div>
        <div className="flex-shrink-0 mt-1">
          {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-slate-100 dark:border-[#21262D]">
          <p className="text-sm text-slate-600 dark:text-[#8B949E] mt-3 leading-relaxed">{insight.description}</p>

          <div className="flex items-center gap-4 mt-3">
            {insight.affected_locations?.length > 0 && (
              <div className="flex items-center gap-1.5">
                <MapPin className="w-3 h-3 text-slate-400" />
                <span className="text-[11px] text-slate-500 dark:text-[#8B949E]">
                  {insight.affected_locations.join(', ')}
                </span>
              </div>
            )}
            <div className="flex items-center gap-1.5">
              <Shield className="w-3 h-3 text-slate-400" />
              <span className="text-[11px] text-slate-500 dark:text-[#8B949E]">Betrouwbaarheid: {confCfg.label}</span>
              <div className="w-12 h-1.5 rounded-full bg-slate-200 dark:bg-[#21262D]">
                <div className={`h-full rounded-full ${confCfg.class} ${confCfg.width}`} />
              </div>
            </div>
          </div>

          {insight.reviewed_by && (
            <p className="text-[10px] text-slate-400 dark:text-[#484F58] mt-2">
              Beoordeeld door {insight.reviewed_by} op {new Date(insight.reviewed_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' })}
            </p>
          )}

          {insight.status === 'new' && (
            <div className="flex items-center gap-2 mt-4">
              <button
                data-testid={`insight-accept-${insight.insight_id}`}
                onClick={() => onUpdate(insight.insight_id, 'accepted')}
                disabled={updating}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-50 dark:bg-green-500/10 text-green-700 dark:text-green-400 text-xs font-medium hover:bg-green-100 dark:hover:bg-green-500/20 transition-colors disabled:opacity-50"
              >
                <Check className="w-3.5 h-3.5" />
                Accepteren
              </button>
              <button
                data-testid={`insight-dismiss-${insight.insight_id}`}
                onClick={() => onUpdate(insight.insight_id, 'dismissed')}
                disabled={updating}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-50 dark:bg-slate-500/10 text-slate-500 dark:text-slate-400 text-xs font-medium hover:bg-slate-100 dark:hover:bg-slate-500/20 transition-colors disabled:opacity-50"
              >
                <X className="w-3.5 h-3.5" />
                Afwijzen
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AiInzichten() {
  const [insights, setInsights] = useState([]);
  const [total, setTotal] = useState(0);
  const [newCount, setNewCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const { authAxios, user } = useAuth();

  const fetchInsights = useCallback(async () => {
    try {
      const api = authAxios();
      const params = new URLSearchParams();
      if (filterType) params.append('type', filterType);
      if (filterStatus) params.append('status', filterStatus);
      params.append('limit', '100');
      const res = await api.get(`/ai/insights?${params.toString()}`);
      setInsights(res.data.insights || []);
      setTotal(res.data.total || 0);
      setNewCount(res.data.new_count || 0);
    } catch (e) {
      console.error('Failed to fetch insights:', e);
    } finally {
      setLoading(false);
    }
  }, [authAxios, filterType, filterStatus]);

  useEffect(() => { fetchInsights(); }, [fetchInsights]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const api = authAxios();
      const res = await api.post('/ai/insights/generate');
      if (res.data.status === 'success') {
        toast.success(`${res.data.count} nieuwe inzichten gegenereerd`);
        fetchInsights();
      } else {
        toast.info(res.data.reason || 'Geen nieuwe inzichten');
      }
    } catch (e) {
      toast.error('Genereren mislukt');
    } finally {
      setGenerating(false);
    }
  };

  const handleUpdate = async (insightId, newStatus) => {
    setUpdating(true);
    try {
      const api = authAxios();
      await api.put(`/ai/insights/${insightId}`, { status: newStatus });
      toast.success(newStatus === 'accepted' ? 'Inzicht geaccepteerd' : 'Inzicht afgewezen');
      fetchInsights();
    } catch (e) {
      toast.error('Bijwerken mislukt');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-[1000px]" data-testid="ai-inzichten-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#2E75B6]/10 dark:bg-[#2E75B6]/20 flex items-center justify-center">
            <Lightbulb className="w-5 h-5 text-[#2E75B6]" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-[#E6EDF3]">AI Inzichten</h2>
            <p className="text-xs text-slate-500 dark:text-[#8B949E]">
              {total} inzichten totaal &middot; {newCount} nieuw
            </p>
          </div>
        </div>
        {user?.role === 'owner' && (
          <button
            data-testid="generate-insights-button"
            onClick={handleGenerate}
            disabled={generating}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#2E75B6] text-white text-sm font-medium hover:bg-[#2565A0] transition-colors disabled:opacity-50"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {generating ? 'Analyseren...' : 'Genereer inzichten'}
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap" data-testid="insight-filters">
        <div className="flex items-center gap-1.5">
          <Filter className="w-3.5 h-3.5 text-slate-400" />
          <span className="text-xs text-slate-500 dark:text-[#8B949E]">Filter:</span>
        </div>
        <select
          data-testid="filter-type"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 dark:border-[#30363D] bg-white dark:bg-[#161B22] text-slate-700 dark:text-[#C9D1D9]"
        >
          <option value="">Alle typen</option>
          {Object.entries(INSIGHT_CONFIG).map(([key, cfg]) => (
            <option key={key} value={key}>{cfg.label}</option>
          ))}
        </select>
        <select
          data-testid="filter-status"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 dark:border-[#30363D] bg-white dark:bg-[#161B22] text-slate-700 dark:text-[#C9D1D9]"
        >
          <option value="">Alle statussen</option>
          {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
            <option key={key} value={key}>{cfg.label}</option>
          ))}
        </select>
      </div>

      {/* Insights List */}
      {insights.length === 0 ? (
        <div className="text-center py-16 bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#21262D]" data-testid="insights-empty-state">
          {(filterType || filterStatus) ? (
            <>
              <Lightbulb className="w-10 h-10 mx-auto text-slate-300 dark:text-[#30363D] mb-3" />
              <p className="text-sm text-slate-500 dark:text-[#8B949E]">
                Geen inzichten gevonden voor dit filter
              </p>
              <p className="text-xs text-slate-400 dark:text-[#484F58] mt-1">
                Pas de filters aan om meer inzichten te zien
              </p>
            </>
          ) : (
            <>
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-amber-50 dark:bg-amber-500/10 flex items-center justify-center relative">
                <Lightbulb className="w-8 h-8 text-amber-500" />
                <Sparkles className="w-4 h-4 text-amber-400 absolute -top-1 -right-1" />
              </div>
              <h3 className="text-base font-heading font-semibold text-slate-900 dark:text-[#E6EDF3] mb-2">
                Vault analyseert je compliance data elke maandag
              </h3>
              <p className="text-sm text-slate-500 dark:text-[#8B949E] max-w-md mx-auto leading-relaxed">
                Hoe meer compliance items je toevoegt, hoe slimmere inzichten Vault kan genereren.
                Start met het invullen van keuringen en contractdeadlines.
              </p>
              <button
                onClick={handleGenerate}
                disabled={generating}
                className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#2E75B6] text-white text-sm font-medium hover:bg-[#27649C] transition-colors disabled:opacity-50"
                data-testid="generate-insights-empty-button"
              >
                {generating ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Genereren...</>
                ) : (
                  <><Sparkles className="w-4 h-4" /> Genereer Inzichten</>
                )}
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-3" data-testid="insights-list">
          {insights.map((insight) => (
            <InsightCard
              key={insight.insight_id}
              insight={insight}
              onUpdate={handleUpdate}
              updating={updating}
            />
          ))}
        </div>
      )}
    </div>
  );
}
